import { Signal } from '@angular/core';
import { Brand } from '@tourfold/libraries/tf-api-client-public-v2';
import { providePageContext } from '@tourfold/shared-kernels/page-context';

const MAX_VISIBLE_BRANDS = 25;

export interface BrandsAiPageContextSignals {
  readonly brands: Signal<Brand[]>;
  readonly searchQuery: Signal<string>;
  readonly sortField: Signal<string | null>;
  readonly sortOrder: Signal<number>;
  readonly originalBrand: Signal<Brand | null>;
  readonly isEditDialogVisible: Signal<boolean>;
}

/**
 * Registers AI assistant page context for the brands settings list.
 *
 * - `entity` is set to the brand currently being edited (dialog open). The
 *   create dialog leaves `entity` undefined — the user has no specific
 *   brand in focus yet.
 * - `visible_rows` carries the first 25 brands so the assistant can
 *   reference them by name without an extra MCP fetch.
 * - `filters` reflects search and sort state.
 *
 * See frontend/AGENTS.md → "AI Assistant Page Context" for the field rules.
 */
export function provideBrandsAiPageContext(state: BrandsAiPageContextSignals): void {
  providePageContext(() => {
    const editing = state.isEditDialogVisible() ? state.originalBrand() : null;
    const sortField = state.sortField();
    const sortOrder = state.sortOrder();
    const search = state.searchQuery().trim();

    return {
      entity: editing
        ? {
            type: 'brand',
            id: editing.id,
            label: editing.name,
          }
        : undefined,
      visible_rows: state
        .brands()
        .slice(0, MAX_VISIBLE_BRANDS)
        .map((brand) => ({
          type: 'brand',
          id: brand.id,
          label: brand.name,
        })),
      filters: {
        ...(search.length > 0 && { search }),
        ...(sortField &&
          sortOrder !== 0 && {
            sort: `${sortField}:${sortOrder === 1 ? 'asc' : 'desc'}`,
          }),
      },
    };
  });
}
